import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import AppLogo from './AppLogo';
import { Button } from './ui/button';
import { verifyMagicToken, verifyConfirmToken } from '../lib/session';

const STASH_KEY = 'ihsanone_claim_pending';

// Verify-on-click interstitial for both flows:
//   - Sign-in magic link:            `?continue=1#th=<token_hash>&em=<email>`
//   - First-time signup confirmation: `?confirm=1#th=<token_hash>&em=<email>`
// In both cases the token_hash sits in the URL FRAGMENT (never sent to a
// server, never logged) and is only exchanged for a real session once a human
// taps the CTA. This stops mail-security scanners that prefetch links from
// silently burning a one-tap sign-in — a prefetch never runs our onClick
// handler, so it never calls GoTrue /verify.
function parseTrigger() {
  try {
    const search = new URLSearchParams(window.location.search);
    const hasContinue = search.get('continue') === '1';
    const hasConfirm = search.get('confirm') === '1';
    const rawHash = (window.location.hash || '').replace(/^#/, '');
    if (!rawHash) return null;
    const params = new URLSearchParams(rawHash);

    const error = params.get('error') || params.get('error_description');
    if (error) return { mode: 'error' };

    const th = params.get('th');
    if (th && (hasContinue || hasConfirm)) {
      const em = params.get('em') || '';
      // `flow` decides which /verify type to call and which copy to show —
      // sign-in continuation vs first-time signup confirmation.
      const flow = hasConfirm ? 'confirm' : 'signin';
      return { mode: 'verify', th, em, flow };
    }
    return null;
  } catch {
    return null;
  }
}

// Strip the trigger + fragment so a refresh or a copy-pasted URL never
// re-triggers the gate or re-exposes the (now-spent) token_hash.
function cleanUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('continue');
    url.searchParams.delete('confirm');
    url.hash = '';
    window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* no-op */ }
}

/**
 * Verify-on-click gate. Mounted globally, right before ClaimGate: on a
 * successful verify it stashes the same `ihsanone_claim_pending` flag
 * ClaimGate's own funnel uses and hands off to it (via the session change +
 * the pending flag) to auto-claim and show the badge. Renders nothing when
 * there's no trigger in the URL.
 */
export default function ContinueGate() {
  const [trigger, setTrigger] = useState(null); // { mode, th, em, flow } | { mode: 'error' }
  const [status, setStatus] = useState('idle'); // idle | verifying | error

  useEffect(() => {
    const t = parseTrigger();
    if (!t) return;
    setTrigger(t);
    if (t.mode === 'error') setStatus('error');
  }, []);

  if (!trigger) return null;

  const isConfirm = trigger.flow === 'confirm';
  const handleClick = async () => {
    if (status === 'verifying') return;
    setStatus('verifying');
    // Set the claim-pending flag BEFORE verifying: verifyMagicToken /
    // verifyConfirmToken writes the session (which fires ClaimGate's
    // session-change effect), so the flag must already be present when
    // that effect runs, or ClaimGate misses the hand-off.
    try { sessionStorage.setItem(STASH_KEY, '1'); } catch { /* private mode */ }
    try {
      const verifier = isConfirm ? verifyConfirmToken : verifyMagicToken;
      const ok = await verifier(trigger.th);
      if (!ok) {
        try { sessionStorage.removeItem(STASH_KEY); } catch { /* private mode */ }
        setStatus('error');
        return;
      }
      cleanUrl();
      setTrigger(null);
    } catch {
      setStatus('error');
    }
  };

  const isError = status === 'error';
  const heading = isConfirm ? 'Confirm your email' : 'Continue to your IhsanOne badge';
  const idleLabel = isConfirm ? 'Confirm my email' : 'Continue';
  const workingLabel = isConfirm ? 'Confirming…' : 'Signing you in…';

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[75] flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      aria-label={isConfirm ? 'Confirm email' : 'Continue sign-in'}
      data-testid={isConfirm ? 'confirm-gate' : 'continue-gate'}
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
        <div className="p-6 text-center">
          <AppLogo size="lg" className="mx-auto mb-3" />
          <h1 className="text-xl font-extrabold text-[#0F172A]">IhsanOne</h1>

          {!isError && (
            <>
              <p className="text-sm text-muted-foreground mt-1.5">{heading}</p>
              {trigger.em && (
                <p className="text-sm mt-4 text-[#0F172A]">
                  {isConfirm ? 'Confirming ' : 'Signing in as '}
                  <span className="font-semibold">{trigger.em}</span>
                </p>
              )}
              <Button
                type="button"
                data-testid={isConfirm ? 'confirm-gate-button' : 'continue-gate-button'}
                onClick={handleClick}
                disabled={status === 'verifying'}
                className="w-full h-12 text-base mt-5"
                size="lg"
              >
                {status === 'verifying' ? workingLabel : idleLabel}
              </Button>
            </>
          )}

          {isError && (
            <div className="mt-4" data-testid={isConfirm ? 'confirm-gate-error' : 'continue-gate-error'}>
              <div className="w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <p className="text-sm font-semibold text-[#0F172A]">
                This link has expired or was already used.
              </p>
              <p className="text-sm text-muted-foreground mt-1.5">
                {isConfirm
                  ? 'Sign up again, or request a new confirmation email.'
                  : 'Ask the organiser to resend your link.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
